import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, Button, Container, Row, Col, Alert, Card, Tabs, Tab, ButtonGroup } from 'react-bootstrap'
import { formatErrorResponse } from '../utils/errorHandlers'
import { issueDestinationAPI } from '../api/issueDestinationAPI'
import ResizableTable from '../components/ResizableTable'
import { highlightText } from '../utils/commonUtils'
import { usePermissions } from '../contexts/PermissionContext'

function IssueDestinationDetailsScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const { id } = useParams()
    const navigate = useNavigate()
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(true)
    const [destination, setDestination] = useState({
        name: '',
        full_name: '',
        contact: '',
        bill_to_address: '',
        ship_to_address: '',
        phone: '',
        email: '',
        is_active: true
    })
    const [subcategories, setSubcategories] = useState([])

    useEffect(() => {
        async function fetchDestination() {
            try {
                setLoading(true)
                const data = await issueDestinationAPI.getById(id);
                // Ensure all string fields are never null (convert null to empty string)
                setDestination({
                    ...data,
                    name: data.name || '',
                    full_name: data.full_name || '',
                    contact: data.contact || '',
                    bill_to_address: data.bill_to_address || '',
                    ship_to_address: data.ship_to_address || '',
                    phone: data.phone || '',
                    email: data.email || ''
                })
                setSubcategories(data.subcategories || [])
            } catch (error) {
                console.error('Error fetching destination:', error)
                setError('Failed to load destination data')
            } finally {
                setLoading(false)
            }
        }
        fetchDestination()
    }, [id])

    const submitHandler = async (e) => {
        e.preventDefault()
        try {
            await issueDestinationAPI.update(id, destination);
            navigate('/issue-destinations')
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    const handleToggleActive = async (e) => {
        e.preventDefault();
        
        if (destination.is_active) {
            if (!window.confirm('Are you sure you want to deactivate this destination?')) {
                return;
            }
        }

        try {
            const updatedData = {
                ...destination,
                is_active: !destination.is_active
            };
            const updatedDestination = await issueDestinationAPI.update(id, updatedData);
            // Ensure all string fields are never null (convert null to empty string)
            setDestination({
                ...updatedDestination,
                name: updatedDestination.name || '',
                full_name: updatedDestination.full_name || '',
                contact: updatedDestination.contact || '',
                bill_to_address: updatedDestination.bill_to_address || '',
                ship_to_address: updatedDestination.ship_to_address || '',
                phone: updatedDestination.phone || '',
                email: updatedDestination.email || ''
            });
        } catch (error) {
            console.error('Error toggling destination status:', error);
            alert(`Failed to update destination: ${error.response?.data?.error || error.response?.data?.message || error.message}`);
        }
    };

    const handleDelete = async (e) => {
        e.preventDefault();
        
        if (!window.confirm('Are you sure you want to permanently delete this destination? This action cannot be undone.')) {
            return;
        }

        try {
            await issueDestinationAPI.delete(id);
            navigate('/issue-destinations');
        } catch (error) {
            console.error('Error deleting destination:', error);
            alert(`Failed to delete destination: ${error.response?.data?.error || error.response?.data?.message || error.message}`);
        }
    };

    const handleSubcategoryToggleActive = async (subcategoryId) => {
        try {
            const subcategory = subcategories.find(sub => sub._id === subcategoryId);
            if (!subcategory) return;

            const updatedData = {
                ...subcategory,
                is_active: !subcategory.is_active
            };
            const updatedSubcategory = await issueDestinationAPI.subcategories.update(subcategoryId, updatedData);
            setSubcategories(subcategories.map(sub => 
                sub._id === subcategoryId ? updatedSubcategory : sub
            ));
        } catch (error) {
            console.error('Error toggling subcategory status:', error);
            alert(`Failed to update subcategory: ${error.response?.data?.error || error.response?.data?.message || error.message}`);
        }
    };

    const handleSubcategoryDelete = async (subcategoryId) => {
        const subcategory = subcategories.find(sub => sub._id === subcategoryId);
        if (!subcategory) return;

        if (!window.confirm(`Are you sure you want to permanently delete the subcategory "${subcategory.name}"? This action cannot be undone.`)) {
            return;
        }

        try {
            await issueDestinationAPI.subcategories.delete(subcategoryId);
            // Remove from list and stay on the same parent page
            setSubcategories(subcategories.filter(sub => sub._id !== subcategoryId));
        } catch (error) {
            console.error('Error deleting subcategory:', error);
            alert(`Failed to delete subcategory: ${error.response?.data?.error || error.response?.data?.message || error.message}`);
        }
    };

    if (permissionsLoading || loading) {
        return (
            <Container className="py-4">
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </Container>
        )
    }

    if (!hasPermission('destinations.modify')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to update issue destinations.
                </Alert>
            </Container>
        )
    }

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h1>Issue Destination Details</h1>
                </Col>
                <Col xs="auto">
                    <Button 
                        variant="outline-primary"
                        onClick={() => navigate('/issue-destinations/subcategory/create', { 
                            state: { parentDestination: destination } 
                        })}
                        className="me-2"
                    >
                        Add Subcategory
                    </Button>
                    <Button 
                        variant="outline-primary"
                        onClick={() => navigate('/issue-destinations')}
                    >
                        <i className="fas fa-arrow-left me-2"></i>
                        Back to List
                    </Button>
                </Col>
            </Row>

            <Tabs defaultActiveKey="details" className="mb-4">
                <Tab eventKey="details" title="Details">
                    <Card>
                        <Card.Body>
                            <Form onSubmit={submitHandler}>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-4" controlId="name">
                                            <Form.Label>Name*</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter name"
                                                value={destination.name}
                                                onChange={(e) => setDestination({ ...destination, name: e.target.value })}
                                                disabled={!destination.is_active}
                                                style={{ opacity: destination.is_active ? 1 : 0.6 }}
                                                required
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-4" controlId="full_name">
                                            <Form.Label>Full Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter full name"
                                                value={destination.full_name}
                                                onChange={(e) => setDestination({ ...destination, full_name: e.target.value })}
                                                disabled={!destination.is_active}
                                                style={{ opacity: destination.is_active ? 1 : 0.6 }}
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-4" controlId="contact">
                                            <Form.Label>Contact</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter contact"
                                                value={destination.contact}
                                                onChange={(e) => setDestination({ ...destination, contact: e.target.value })}
                                                disabled={!destination.is_active}
                                                style={{ opacity: destination.is_active ? 1 : 0.6 }}
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-4" controlId="bill_to_address">
                                            <Form.Label>Bill To Address</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter billing address"
                                                value={destination.bill_to_address}
                                                onChange={(e) => setDestination({ ...destination, bill_to_address: e.target.value })}
                                                disabled={!destination.is_active}
                                                style={{ opacity: destination.is_active ? 1 : 0.6 }}
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-4" controlId="ship_to_address">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <Form.Label className="mb-0">Ship To Address</Form.Label>
                                                {destination.bill_to_address && destination.is_active && (
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        onClick={() => setDestination({...destination, ship_to_address: destination.bill_to_address})}
                                                        style={{ fontSize: '0.875rem', padding: '0' }}
                                                    >
                                                        <i className="bi bi-clipboard me-1"></i>
                                                        Copy from Bill To
                                                    </Button>
                                                )}
                                            </div>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter shipping address"
                                                value={destination.ship_to_address}
                                                onChange={(e) => setDestination({ ...destination, ship_to_address: e.target.value })}
                                                disabled={!destination.is_active}
                                                style={{ opacity: destination.is_active ? 1 : 0.6 }}
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-4" controlId="phone">
                                            <Form.Label>Phone</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter phone"
                                                value={destination.phone}
                                                onChange={(e) => setDestination({ ...destination, phone: e.target.value })}
                                                disabled={!destination.is_active}
                                                style={{ opacity: destination.is_active ? 1 : 0.6 }}
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-4" controlId="email">
                                            <Form.Label>Email</Form.Label>
                                            <Form.Control
                                                type="email"
                                                placeholder="Enter email"
                                                value={destination.email}
                                                onChange={(e) => setDestination({ ...destination, email: e.target.value })}
                                                disabled={!destination.is_active}
                                                style={{ opacity: destination.is_active ? 1 : 0.6 }}
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {error && (
                                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                        {error}
                                    </Alert>
                                )}

                                <Row className="mt-3">
                                    <Col>
                                        {destination.is_active ? (
                                            <>
                                                {!destination.is_system && (
                                                    <Button
                                                        variant="primary"
                                                        type="submit"
                                                        className="me-2"
                                                    >
                                                        Update
                                                    </Button>
                                                )}
                                                {!destination.is_system && (
                                                    <Button
                                                        variant="danger"
                                                        onClick={handleToggleActive}
                                                        className="me-2"
                                                    >
                                                        Deactivate
                                                    </Button>
                                                )}
                                                {!destination.is_system && (
                                                    <Button
                                                        variant="outline-danger"
                                                        onClick={handleDelete}
                                                        className="me-2"
                                                    >
                                                        Delete
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="outline-primary"
                                                    onClick={() => navigate('/issue-destinations')}
                                                >
                                                    <i className="fas fa-arrow-left me-2"></i>
                                                    Back to List
                                                </Button>
                                            </>
                                        ) : (
                                            !destination.is_system && (
                                                <Button
                                                    variant="success"
                                                    onClick={handleToggleActive}
                                                >
                                                    Activate
                                                </Button>
                                            )
                                        )}
                                    </Col>
                                </Row>

                                {!destination.is_active && (
                                    <Alert variant="warning" className="mt-3">
                                        This destination is currently inactive. Activate it to make changes.
                                    </Alert>
                                )}
                            </Form>
                        </Card.Body>
                    </Card>
                </Tab>

                <Tab eventKey="subcategories" title={`Subcategories (${subcategories.length})`}>
                    <Card>
                        <Card.Body>
                            {subcategories.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <p>No subcategories found for this destination.</p>
                                    <Button 
                                        variant="outline-primary"
                                        onClick={() => navigate('/issue-destinations/subcategory/create', { 
                                            state: { parentDestination: destination } 
                                        })}
                                    >
                                        Add First Subcategory
                                    </Button>
                                </div>
                            ) : (
                                <ResizableTable
                                    tableId="issue_destination_subcategories_table"
                                    columns={[
                                        {
                                            id: 'name',
                                            header: 'Name',
                                            cell: (row) => (
                                                <>
                                                    {row.name}
                                                    {!row.is_active && (
                                                        <span className="ms-2 text-muted fst-italic">
                                                            (Inactive)
                                                        </span>
                                                    )}
                                                </>
                                            )
                                        },
                                        {
                                            id: 'full_name',
                                            header: 'Full Name',
                                            cell: (row) => row.full_name || ''
                                        },
                                        {
                                            id: 'contact',
                                            header: 'Contact',
                                            cell: (row) => row.contact || ''
                                        },
                                        {
                                            id: 'phone',
                                            header: 'Phone',
                                            cell: (row) => row.phone || ''
                                        },
                                        {
                                            id: 'email',
                                            header: 'Email',
                                            cell: (row) => row.email || ''
                                        },
                                        {
                                            id: 'actions',
                                            header: 'Actions',
                                            cell: (row) => (
                                                <ButtonGroup size="sm" className="gap-1">
                                                    <Button 
                                                        variant={row.is_active ? "danger" : "success"}
                                                        onClick={() => handleSubcategoryToggleActive(row._id)}
                                                    >
                                                        {row.is_active ? 'Deactivate' : 'Activate'}
                                                    </Button>
                                                    <Button 
                                                        variant="outline-danger"
                                                        onClick={() => handleSubcategoryDelete(row._id)}
                                                    >
                                                        Delete
                                                    </Button>
                                                </ButtonGroup>
                                            )
                                        }
                                    ]}
                                    data={subcategories}
                                    onRowClick={(row) => navigate(`/issue-destinations/subcategory/${row._id}`)}
                                    striped
                                    hover
                                />
                            )}
                        </Card.Body>
                    </Card>
                </Tab>
            </Tabs>
        </Container>
    )
}

export default IssueDestinationDetailsScreen